import type {
  AssetReviewItem,
  AssetReviewSnapshot,
  AssetReviewStage,
} from '@volley/crucible-ipc-types';

/**
 * #2058 — staged asset-review projections.
 *
 * The pure, React-free, IPC-free helpers the staged asset-review surfaces key
 * off. Extracted from the (now-removed) ChatAssetReviewCard so the live
 * `TvSteeringSurface` can consume them without dragging in a dead component.
 * Both are unit-tested in `asset-stage-view.test.tsx` (node-env SSR-only).
 */

/**
 * Pure projection of a snapshot into "what the surface shows right now" (#2058):
 * the current stage, the assets that belong to it, and the human-facing
 * "Stage N of M" label. No React, no IPC.
 *
 * The surface renders ONLY `assets` from this view. A snapshot with no stages
 * (an older backend, or an empty set) degrades to showing every asset (the
 * pre-staged behaviour) so the surface never blanks.
 */
export function currentStageView(snapshot: AssetReviewSnapshot): {
  stage: AssetReviewStage | null;
  assets: AssetReviewItem[];
  stageNumber: number;
  stageCount: number;
  allStagesApproved: boolean;
} {
  const stages = snapshot.stages ?? [];
  const stageCount = stages.length;
  // No stages → show everything (graceful fallback for a pre-#2058 snapshot).
  if (stageCount === 0) {
    return {
      stage: null,
      assets: snapshot.assets,
      stageNumber: 0,
      stageCount: 0,
      allStagesApproved: false,
    };
  }
  const idx = snapshot.currentStageIndex;
  const allStagesApproved = idx >= stageCount;
  // When every stage is approved, keep showing the LAST stage's assets (so the
  // surface doesn't blank during the "finishing…" beat) but flag completion.
  const activeIndex = allStagesApproved ? stageCount - 1 : idx;
  const stage = stages[activeIndex] ?? null;
  const stageAssets = snapshot.assets.filter((a) => a.stageIndex === activeIndex);
  // #2671: the solo gate is a COST guard for the stage's first asset only — it
  // lets the user redirect style before the rest of the stage spends. Once one
  // member is approved (style locked), the remaining batch (approved +
  // reviewable + still-generating) renders together so the surface can grid
  // it, rather than re-collapsing every subsequent asset back to one.
  const assets =
    !allStagesApproved && stage?.mode === 'approve-each' && (stage?.approved ?? 0) === 0
      ? oneAtATimeStageAssets(stageAssets)
      : stageAssets;
  return {
    stage,
    assets,
    stageNumber: activeIndex + 1,
    stageCount,
    allStagesApproved,
  };
}

function oneAtATimeStageAssets(assets: AssetReviewItem[]): AssetReviewItem[] {
  const approved = assets.filter((asset) => asset.status === 'approved');
  const active =
    assets.find((asset) => asset.reviewable && asset.status !== 'approved') ??
    assets.find((asset) => asset.status !== 'approved') ??
    approved[approved.length - 1] ??
    assets[0];
  return active ? [active] : [];
}

/**
 * The NO→feedback submit guard (#2058): trim the typed feedback and, only when
 * it's non-empty, dispatch a regenerate for this asset. An empty/whitespace note
 * is a no-op (a "No" with nothing to act on shouldn't burn a regeneration). The
 * dispatched `feedback` is folded into the engine's img2img prompt so the regen
 * is a genuine change.
 *
 * Returns the trimmed feedback that was dispatched, or null if it no-op'd —
 * lets a caller/test assert what happened.
 */
export function submitAssetFeedback(
  assetId: string,
  rawFeedback: string,
  regenerate: (id: string, feedback: string) => void,
): string | null {
  const fb = rawFeedback.trim();
  if (!fb) return null;
  regenerate(assetId, fb);
  return fb;
}
